import React, { useState } from 'react';
import { useTheme } from './ThemeProvider';

interface ThemeSelectorProps {
  className?: string;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ className }) => {
  const { theme, currentThemeName, switchAllowed, availableThemes, switchTheme, isSwitching } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  // Don't render if theme switching is not allowed
  if (!switchAllowed || !availableThemes || availableThemes.length === 0) {
    return null;
  }

  const handleThemeChange = async (newTheme: string) => {
    if (isLoading || isSwitching) return;
    
    setIsLoading(true);
    try {
      await switchTheme(newTheme);
    } catch (error) {
      console.error('Failed to switch theme:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Use the tracked current theme name or default to first available theme
  const selectedThemeName = currentThemeName || availableThemes[0] || '';

  return (
    <div className={`theme-selector ${className || ''} ${(isLoading || isSwitching) ? 'loading' : ''}`}>
      <select
        value={selectedThemeName}
        onChange={(e) => handleThemeChange(e.target.value)}
        disabled={isLoading || isSwitching}
        className="theme-select"
      >
        {availableThemes.map((themeName) => (
          <option key={themeName} value={themeName}>
            {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
          </option>
        ))}
      </select>
      
      <style>{`
        .theme-selector {
          position: relative;
          display: inline-block;
        }
        
        .theme-select {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: var(--brand-color, #2090FF);
          padding: 8px 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          outline: none;
          transition: all 0.2s ease;
          min-width: 120px;
        }
        
        .theme-select:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }
        
        .theme-select:focus {
          background: rgba(255, 255, 255, 0.2);
          border-color: var(--brand-color, #2090FF);
          box-shadow: 0 0 0 2px var(--brand-color, #2090FF)33;
        }
        
        .theme-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Loading indicator */
        .theme-selector.loading::after {
          content: '';
          position: absolute;
          top: 50%;
          right: 30px;
          width: 12px;
          height: 12px;
          border: 2px solid transparent;
          border-top: 2px solid var(--brand-color, #2090FF);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          transform: translateY(-50%);
        }
        
        @keyframes spin {
          0% { transform: translateY(-50%) rotate(0deg); }
          100% { transform: translateY(-50%) rotate(360deg); }
        }
        
        .theme-select option {
          background: var(--background-color, #313131);
          color: white;
          padding: 8px;
        }
      `}</style>
    </div>
  );
};